/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { assignStJudeGlobal, wrapStJudeGlobal } from '@/lib/tracking/stjude-datalayer';
import { useStJudeRootLoaderData } from './use-stjude-root-loader-data';
import { getBarePath } from '@/utils/seo';
import { routes } from '@/route-paths';

/**
 * Routes with their own dedicated sync component that corrects `page` and/or
 * adds extra fields (`product[]`, `search`, `cart.item[]`, `transaction`)
 * using data only that route has: `StJudeCategoryDataLayerSync`,
 * `StJudeProductDataLayerSync`, `StJudeCheckoutDataLayerSync`,
 * `StJudeSearchDataLayerSync`, `StJudeBasketProductDataLayerSync` (cart and
 * login), `StJudeOrderConfirmationDataLayerSync`. This component must not
 * write over any of these — see the skip check below.
 */
const ROUTES_WITH_DEDICATED_SYNC = [
    routes.category,
    routes.product,
    routes.checkout,
    routes.search,
    routes.cart,
    routes.login,
    routes.orderConfirmation,
];

/**
 * Keeps `window.stjude.digitalData` in sync with client-side (SPA) navigation.
 *
 * `<StJudeDataLayer>` (rendered in `root.tsx`'s `<head>`) only sets
 * `window.stjude` on the very first document parse: on every subsequent
 * client-side route change, React re-renders that component with fresh
 * loader data, but it only mutates the *existing* `<script>` element's
 * content — browsers execute a `<script>` element's body when it's first
 * inserted into the document, not when its content is mutated afterward. So
 * without this component, `window.stjude` would silently freeze at whatever
 * page loaded first (e.g. always `home`).
 *
 * `root.tsx`'s loader has no `shouldRevalidate` override, so React Router's
 * default applies: it re-runs on every navigation, recomputing
 * `stJudeDigitalData` (page, user/email, sysEnv) for the destination URL.
 * This component just re-applies that already-fresh value to the global
 * imperatively whenever it changes — the same "react to router state in a
 * `useEffect`" pattern `PageViewTracker` uses, for the same underlying reason
 * (head-injected script re-execution can't be relied on for SPA nav).
 *
 * Skips every route with a dedicated sync component (see
 * `ROUTES_WITH_DEDICATED_SYNC`): those have data (breadcrumb, checkout step,
 * search results, basket contents, order details) this component doesn't
 * have, and — because this component is mounted as a later sibling of
 * `<Outlet>` in `root.tsx` — its effect fires *after* those nested
 * components' effects within the same navigation commit, so writing here
 * would silently clobber the more accurate/enriched value with this generic
 * one on every navigation into those routes.
 *
 * Renders nothing (non-visual); mount once near the root, client-side only.
 */
export function StJudeDataLayerSync() {
    const location = useLocation();
    const config = useConfig();
    const { site, locale } = useSite();
    const rootData = useStJudeRootLoaderData();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!rootData?.omnitureConfig?.omnitureEnabled || !rootData.stJudeDigitalData) return;

        const barePathname = getBarePath({
            pathname: location.pathname,
            urlConfig: config.url,
            currentSiteRef: site.alias ?? site.id,
            currentLocaleRef: locale.alias ?? locale.id,
        });
        const hasDedicatedSync = ROUTES_WITH_DEDICATED_SYNC.some((pattern) => matchPath(pattern, barePathname));
        if (hasDedicatedSync) return;

        assignStJudeGlobal(wrapStJudeGlobal(rootData.stJudeDigitalData));
    }, [location.pathname, config.url, site, locale, rootData]);

    return null;
}
